import { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import Loading from '@components/common/Loading';
import { crmService } from '@services/crmService';

interface ProtectedRouteProps {
  children: React.ReactNode;
}

export default function ProtectedRoute({ children }: ProtectedRouteProps) {
  const location = useLocation();
  const [isAuthenticated, setIsAuthenticated] = useState<boolean | null>(null);
  const [isChecking, setIsChecking] = useState(true);

  useEffect(() => {
    const checkAuth = async () => {
      // Always start with false - require explicit authentication
      let authenticated = false;

      // IMMEDIATE CHECK: If no token, fail fast
      const token = sessionStorage.getItem('crm_token');
      if (!token) {
        // No token - immediately not authenticated, don't even try API
        setIsAuthenticated(false);
        setIsChecking(false);
        return;
      }

      // Token exists - verify it's valid by calling API
      try {
        const response = await crmService.getCurrentUser();
        // Only consider authenticated if:
        // 1. We get a valid response
        // 2. Response has a user object
        // 3. User is NOT using legacy token (legacy_token should not exist)
        if (response && response.user) {
          // Explicitly reject legacy tokens
          if (response.user.legacy_token === true) {
            authenticated = false;
            crmService.logout();
          } else {
            // Valid JWT token with real user
            authenticated = true;
          }
        } else {
          authenticated = false;
          crmService.logout();
        }
      } catch {
        // API call failed - token invalid or expired
        authenticated = false;
        crmService.logout();
      } finally {
        setIsAuthenticated(authenticated);
        setIsChecking(false);
      }
    };

    checkAuth();
  }, []);

  // Show loading while checking
  if (isChecking) {
    return <Loading fullScreen text="Checking authentication..." />;
  }

  // Redirect to login if not authenticated
  if (!isAuthenticated) {
    return <Navigate to="/admin/login" state={{ from: location }} replace />;
  }

  // Only render children if authenticated
  return <>{children}</>;
}

